import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../api/store";
import { ComputationTabs, RatesUpdateType, TypeOfRates } from "../../types/type";
import { evidenceOfInsurability, gcliRates6670InitialState, gcliRates7175InitialState, gcliRates7680InitialState } from "../../data/actuarial";
import { RatesState, RiderLegend, RateGroup, RateItem, RatesInfo, UpdateAmountPayload, GCLIRates1865BasePlan } from "./rates-state";

const initialState: RatesState = {
    ridersLegend: [],
    ratesOf1865: [],
    rates1865: null,
    rates6670: null,
    rates7175: null,
    rates7680: null,
    gcliRates6670: gcliRates6670InitialState,
    gcliRates7175: gcliRates7175InitialState,
    gcliRates7680: gcliRates7680InitialState,
    showRates: null,
    evidenceOfInsurability: evidenceOfInsurability,
    isUpdate: false,
    ratesTabs: 'info',
    rateFile: null,

    amountWarning: {
        rates1865: null,
        rates6670: null,
        rates7175: null,
        rates7680: null,
    },
    bulkData: {
        isBulkFillOpen: false,
        activeAgeBracket: null,
        selectedMonths: [],
        bulkValue: 'N/A',
        bulkWarning: null,
    }
};

const ratesSlice = createSlice({
    name: "ratesSlice",
    initialState,
    reducers: {
        setRateFile: (state, action: PayloadAction<File | null>) => {
            state.rateFile = action.payload;
        },
        resetRateFile: (state) => {
            state.rateFile = null
        },
        setRidersLegend: (state, action: PayloadAction<RiderLegend[]>) => {
            state.ridersLegend = action.payload;
        },
        //ratesOf1865
        addRateData: ( state, action: PayloadAction<{ title: string; items: RateItem[]; }> ) => {
            const { title, items } = action.payload;
            const group = state.ratesOf1865.find(
                (rate) => rate.title === title
            );
            if (group) {
                group.data = items;
            }
        },
        setRateGroups: (state, action: PayloadAction<RateGroup[]>) => {
            state.ratesOf1865 = action.payload;
        },
        updateRateAmountOfRate1865: ( state, action: PayloadAction<{ title: string; id: number; amount: string; type: RatesUpdateType; }> ) => {
            const { title, id, amount, type } = action.payload;

            const group = state.ratesOf1865.find((g) => g.title === title);
            if (!group) return;

            const item = group.data.find((i) => {
                if (type === "plan") {
                    return i.type === "plan" && i.plan_id === id;
                }

                return i.type === "rider" && i.rider_id === id;
            });

            if (item) {
                item.amount = amount;
            }
        },
        // ------------------------------
        setRatesTabs: (state, action: PayloadAction<ComputationTabs>) => {
            state.ratesTabs = action.payload;
        },
        setClearRatesTabs: (state) => {
            state.ratesTabs = 'info';
        },

        updateEvidenceCheck: (state, action: PayloadAction<{ id: number; isCheck: boolean }>) => {
            const { id, isCheck } = action.payload;
            
            const item = state.evidenceOfInsurability.find(i => i.id === id);

            if (item) {
                item.isCheck = isCheck;
            }
        },

        setRates1865: (state, action: PayloadAction<RatesInfo[]>) => {
            state.rates1865 = action.payload;
        },
        setRateForAll: (state, action: PayloadAction<{data: RatesInfo[], typeRate: '6670' | '7175' | '7680'}>) => {
            const { data, typeRate } = action.payload;

            if (typeRate === '6670') {
                const groups = state.gcliRates6670 ?? [];
                groups.forEach(group => {
                    group.data = [
                        ...group.data,
                        ...data
                    ];
                });

                state.gcliRates6670 = groups;
            }
            if (typeRate === '7175') {
                const groups = state.gcliRates7175 ?? [];
                groups.forEach(group => {
                    group.data = [
                        ...group.data,
                        ...data
                    ];
                });

                state.gcliRates7175 = groups;
            }
            if (typeRate === '7680') {
                const groups = state.gcliRates7680 ?? [];
                groups.forEach(group => {
                    group.data = [
                        ...group.data,
                        ...data
                    ];
                });

                state.gcliRates7680 = groups;
            }
        },
        setShowRates: (state, action: PayloadAction<{ isShow6670: boolean; isShow7175: boolean; isShow7680: boolean;}>) => {
            state.showRates = action.payload;
        },
        resetRatesBasedOnRatesType: (state, action: PayloadAction<{type: TypeOfRates}>) => {
            const type = action.payload.type;
            if(type === "6670") {
                state.gcliRates6670 = gcliRates6670InitialState;
            }
            if(type === "7175") {
                state.gcliRates7175 = gcliRates7175InitialState;
            }
            if(type === "7680") {
                state.gcliRates7680 = gcliRates7680InitialState;
            }
        },
        resetRates: () => ({ ...initialState }),
        resetAllRates: (state) => {
            state.rates1865 = null;
            state.rates6670 = null;
            state.rates7175 = null;
            state.rates7680 = null;
            state.gcliRates6670 = gcliRates6670InitialState;
            state.gcliRates7175 = gcliRates7175InitialState;
            state.gcliRates7680 = gcliRates7680InitialState;
        },
        updateGcliAmount: (
            state,
            action: PayloadAction<UpdateAmountPayload>
        ) => {
            const { group, title, id, type, amount } = action.payload;

            const selectedGroup = state[group];

            const ageGroup = selectedGroup?.find(
                (item) => item.title === title
            );

            if (!ageGroup) return;

            const rateItem = ageGroup.data.find((item) => {
                if (type === "plan") {
                    return (
                        item.type === "plan" &&
                        item.plan_id === id
                    );
                }

                return (
                    item.type === "rider" &&
                    item.rider_id === id
                );
            });

            if (!rateItem) return;

            rateItem.amount = amount;
        },
        setIsUpdateRates: (state, action: PayloadAction<boolean>) => {
            state.isUpdate = action.payload;
        },

        setGCLIRates6680FromApi: (
            state, 
            action: PayloadAction<{ 
                rateType: "gcliRates6670" | "gcliRates7175" | "gcliRates7680"; 
                apiData: any
            }>
        ) => {
            const { rateType, apiData } = action.payload;
            const currentRates = state[rateType];
            if (!currentRates) return;

            state[rateType] = currentRates.map((ageGroup) => {
                const ageKey = `age_${ageGroup.title}` as keyof typeof apiData;

                const ageRates: GCLIRates1865BasePlan[] = apiData[ageKey] || [];

                return {
                    ...ageGroup,
                    data: ageGroup.data.map((item) => {
                        const matchedRate = ageRates.find(
                            (rate: GCLIRates1865BasePlan) => rate.term_of_months === item.rider_id
                        );

                        return {
                            ...item,
                            amount: matchedRate?.basic_rate ?? item.amount,
                        };
                    }),
                };
            });
        },

        setGYRTAndGPARates6680FromApi: (
            state,
            action: PayloadAction<{
                rateType: "gcliRates6670" | "gcliRates7175" | "gcliRates7680";
                apiData: any;
            }>
        ) => {
            const { rateType, apiData } = action.payload;

            const currentRates = state[rateType];
            if (!currentRates) return;

            state[rateType] = currentRates.map((ageGroup) => {
                const ageKey = `age_${ageGroup.title}` as keyof typeof apiData;

                const ageRates = apiData?.[ageKey];

                if (!Array.isArray(ageRates) || ageRates.length === 0) {
                    return ageGroup;
                }

                const rateInfo = ageRates[0];

                return {
                    ...ageGroup,
                    data: ageGroup.data.map((item) => {
                        // ✅ PLAN ROW (rider_id is null)
                        if (item.type === "plan") {
                            return {
                                ...item,
                                amount: rateInfo.basic_rate ?? '',
                            };
                        }

                        // ✅ RIDER ROWS
                        const rider = rateInfo.riders.find(
                            (r: any) => r.rider_id === item.rider_id
                        );

                        return {
                            ...item,
                            // amount: rider?.rider_rate ?? item.amount ?? "",
                            amount: rider?.rider_rate ?? "",
                        };
                    }),
                };
            });
        },

        setBulkData: (state, action: PayloadAction<Partial<typeof state.bulkData>>) => {
            state.bulkData = { ...state.bulkData, ...action.payload };
        },
        setAmountWarning: ( state, action: PayloadAction<{ rateType: keyof RatesState["amountWarning"]; warning: string | null; }> ) => {
            state.amountWarning[action.payload.rateType] = action.payload.warning;
        },
    },
})

export const {
    setRateFile,
    resetRateFile,

    updateEvidenceCheck,
    resetRates,
    setRates1865,
    setRateForAll,
    setShowRates,
    resetAllRates,
    resetRatesBasedOnRatesType,

    updateGcliAmount,
    setIsUpdateRates,

    setRatesTabs,
    setClearRatesTabs,

    addRateData,
    setRateGroups,
    updateRateAmountOfRate1865,

    setGCLIRates6680FromApi,
    setGYRTAndGPARates6680FromApi,

    setRidersLegend,

    setBulkData,
    setAmountWarning
} = ratesSlice.actions;

export const selectRate1865State = (state: RootState) => state.rates.rates1865;
export const selectRate6670State = (state: RootState) => state.rates.rates6670;
export const selectRate7175State = (state: RootState) => state.rates.rates7175;
export const selectRate7680State = (state: RootState) => state.rates.rates7680;
export const selectEvidenceOfInsurabilityState = (state: RootState) => state.rates.evidenceOfInsurability;
export const selectShowRatesState = (state: RootState) => state.rates.showRates;
export const selectIsUpdateRatesState = (state: RootState) => state.rates.isUpdate;

export const selectGCLIRates6670 = (state: RootState) => state.rates.gcliRates6670;
export const selectGCLIRates7175 = (state: RootState) => state.rates.gcliRates7175;
export const selectGCLIRates7680 = (state: RootState) => state.rates.gcliRates7680;

export const selectActiveRateTab = (state: RootState) => state.rates.ratesTabs;

export const selectRatesOf1865 = (state: RootState) => state.rates.ratesOf1865;
export const selectRidersLegend = (state: RootState) => state.rates.ridersLegend;

export const selectRateFile = (state: RootState) => state.rates.rateFile;

export const selectBulkData = (state: RootState) => state.rates.bulkData;
export const selectAmountWarning = (state: RootState) => state.rates.amountWarning;


export default ratesSlice.reducer;